<template>
    <div class="col-md-3 col-sm-6 mb-sm-4 profile-block profile-block--native new-style-native-advert"
         :class="{'outline-gold': isGoldSize, 'outline-regular': !isGoldSize, 'advert-placeholder': !isStartedToScroll, 'fromProfile': fromProfile}"
         :id="id">
        <span
            @click.prevent="advertClickUrl(advert, '_blank', advert.big_query_tag || 'eb-native-no-tag', impressionId)"
            class="profile-block-link h-100 cursor-pointer">
            <span class="profile-block-images loaded h-100">
                <div class="native-girls-partner-tag" v-if="isStartedToScroll">
                    {{ advert.tag_title ? advert.tag_title : 'Anzeige' }}
                </div>
                <div :style="{'background-image': `url('${advertImageUrl}')`}" class="native-girls-image"></div>
            </span>
        </span>

        <span class="profile-block-details">
            <span class="user-details overflow-hidden">
                <span class="heading-1 d-flex">
                    <span class="text-ellipsis">
                        {{ advertTextLine }}
                    </span>
                </span>
                <span class="user-details-location">
                    <span class="text-ellipsis">
                        {{ advertSubTextLine }}
                    </span>
                </span>
            </span>
        </span>
    </div>
</template>

<script>
import {mapState} from "vuex";
import xtrackingMixin from "@/components/mixins/xtrackingMixin";
import advertMixin from "@/components/mixins/adverts/advertMixin";
import photoMixin from "@/components/mixins/photoMixin";
import {pick} from "lodash/object";
import {incrementNativeAdImpressionCount} from "@/core/utils/helpers/cookieHelper";
import {regionRelativeUrl} from "@/core/utils/helpers/regions/regionUrlHelper";
import {generateImpressionId} from "@/core/utils/helpers/advertHelper";
import {decodeRot13, encodeRot13} from "@/core/utils/helpers/textHelpers";
import {AdvertPlaceholder} from "@/core/constants/advertConstants";

const data = () => {
    return {
        id: generateImpressionId(),
        impressionIds: [],
        impressionId: null,
        isDesktop: true,
        isStartedToScroll: false,
    }
}

function created() {
    this.impressionId = generateImpressionId();

    if (this.currentPage > 1 && !this.hideExplicitRegionAds) {
        this.isStartedToScroll = true;
    }

    if (typeof window !== 'undefined' && !this.hideExplicitRegionAds) {
        window.addEventListener("scroll", this.handleScroll);
    }

    this.isDesktop = utils.isDesktop();
}

function beforeDestroy() {
    if (typeof window !== 'undefined' && !this.hideExplicitRegionAds) {
        window.removeEventListener("scroll", this.handleScroll);
    }
}

function handleScroll() {
    this.isStartedToScroll = true;
}

function observer() {
    if (this.hideExplicitRegionAds) {
        return;
    }

    const id = this.id;
    if (!id || typeof window === "undefined" || !_.get(this, 'advert.imageurl', _.get(this, 'advert.image_full_link'))) {
        return;
    }
    const advertisement = document.getElementById(id);

    if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (!this.impressionIds.includes(id)) {
                        const nativeImpressionCount = incrementNativeAdImpressionCount();
                        const extraAttributes = pick(this.advert, ['id', 'image_full_link', 'link', 'text_line_1', 'text_line_2', 'mobile_text_line_1', 'tag_title'])
                        this.captureTrackingEvent(this.currentUser, (this.fromProfile ? 'profile' : 'search'), 'native-ads-impression', (this.advert.big_query_tag || 'eb-native-no-tag'), null, {
                            position: this.advert.position,
                            sch: this.advert.searchId,
                            'native_impression_count': nativeImpressionCount,
                            'impression_id': this.impressionId,
                            ...extraAttributes,
                        })
                        this.impressionIds.push(id);
                    }
                    observer.disconnect();
                }
            });
        }, {
            threshold: 0.5,
        });
        observer.observe(advertisement);
    }
}

/**
 * @see docs/ads/native-advert-click-url.md — Native Advert Click URL convention.
 * Keep param shape (action, label, sessionId, deviceId, country, category, attributes[impression_id])
 * and ROT13 placeholder swap consistent with `advertMixin#advertClickUrl`
 * and `userProfileMediaMixin#buildGalleryAdvertClickUrl`.
 */
function clickUrl() {
    try {
        let baseUrl = '';
        if (this.advert.clickurl != null) {
            baseUrl = atob(this.advert.clickurl);
        }

        if (!baseUrl) {
            return regionRelativeUrl('/users/registration');
        }

        const url = new URL(baseUrl);

        // Replace click_id placeholders with impression_id
        if (this.impressionId) {
            const placeholder = AdvertPlaceholder.CLICK_ID;
            const placeholderEncoded = encodeURIComponent(placeholder);
            const impressionIdEncoded = encodeURIComponent(this.impressionId);

            const toParam = url.searchParams.get('to');
            if (toParam) {
                const rot13Decoded = decodeRot13(toParam);
                const replaced = rot13Decoded.replace(placeholderEncoded, impressionIdEncoded);
                url.searchParams.set('to', encodeRot13(replaced));
            }
        }

        url.searchParams.append('action', 'native-ads-click');
        url.searchParams.append('label', this.advert.big_query_tag || 'eb-native-no-tag');
        url.searchParams.append('position', this.advert.position);
        url.searchParams.append('sch', this.advert.searchId);

        if (this.fromProfile) {
            url.searchParams.append('category', 'profile');
        }

        if (this.impressionId) {
            url.searchParams.append('attributes[impression_id]', this.impressionId);
        }

        return url;
    } catch (e) {
        return regionRelativeUrl('/users/registration');
    }
}


function getDefaultPlaceholderForAds() {
    return {
        imageurl: "/images/soladaplaceholder.jpg",
        isDefault: true,
    };
}

function advertImageUrl() {
    if (!this.isStartedToScroll) {
        return this.getDefaultPlaceholderForAds().imageurl;
    }

    return this.handleFallbackProductionImageUrl(this.advert.image_full_link, 'erobella-images/images/search_ad/');
}

function advertTextLine() {
    return this.isStartedToScroll ? (!this.isDesktop && this.advert.mobile_text_line_1 ? this.advert.mobile_text_line_1 : this.advert.text_line_1) : '';
}

function advertSubTextLine() {
    return this.isStartedToScroll ? (!this.isDesktop && this.advert.mobile_text_line_2 ? this.advert.mobile_text_line_2 : this.advert.text_line_2) : '';
}

function isGoldSize() {
    return (_.get(this, 'advert.advertSize') || '').includes('gold');
}

export default {
    created,
    data,
    beforeDestroy,
    props: {
        advert: {
            required: true,
        },
        currentPage: {
            required: true,
        },
        fromProfile: {
            type: Boolean,
            required: false,
            default: () => false,
        },
    },
    computed: {
        ...mapState(['currentUser', 'hideExplicitRegionAds']),
        advertImageUrl,
        advertTextLine,
        isGoldSize,
        advertSubTextLine,
    },
    watch: {
        advert: {
            deep: true,
            handler() {
                this.observer()
            },
        },
    },
    mixins: [xtrackingMixin, advertMixin, photoMixin],
    methods: {
        regionRelativeUrl,
        getDefaultPlaceholderForAds,
        handleScroll,
        observer,
    },
}
</script>
